/**
 * Decides whether a Bugsnag error report originates in our own code, used by
 * the apps' `onError` callback to drop injected/extension-script noise (most
 * commonly iOS on-device page translation). Such reports are mutual-recursion
 * `RangeError`s whose frames have no script URL (`file` is `undefined` and the
 * function names are `unknown`), so they can never symbolicate against our
 * uploaded source maps. See issue #183.
 *
 * Kept here (rather than inline in each app.config) so it is shared by `app`
 * and `selfserv` and unit-tested in isolation. It works on plain frame-file
 * strings so the common library keeps no Bugsnag dependency.
 */
export class BugsnagFilter {
  /**
   * Our esbuild output assets: the `main` / `polyfills` entry chunks and the
   * lazy `chunk-<hash>` route chunks. Production builds emit these unhashed
   * (`outputHashing: none`), so `main.js` must match; the optional
   * `[.-]<hash>` segment keeps the match working if hashing is ever enabled.
   */
  private static readonly OWN_BUNDLE_FILE =
    /\/(?:main|polyfills|chunk)[.\-\w]*\.js(?:[?#].*)?$/;

  /** True when at least one stack frame is served from our own bundle. */
  static isFromOwnBundle(frameFiles: readonly (string | undefined)[]): boolean {
    return frameFiles.some(
      (file) =>
        typeof file === "string" && BugsnagFilter.OWN_BUNDLE_FILE.test(file),
    );
  }

  /**
   * Whether a Bugsnag report should be sent. Drops a report only when it has
   * a stack whose frames are ALL foreign: the #183 injected-script signature
   * is a deep `Ok`/`Mk` recursion with frames but no script URL. A stackless
   * report is kept, since a genuine error reported without a usable stack
   * (e.g. a non-`Error` promise rejection) also carries no own-bundle frame,
   * and requiring one would silently drop real errors alongside the noise.
   */
  static shouldReport(frameFiles: readonly (string | undefined)[]): boolean {
    return frameFiles.length === 0 || BugsnagFilter.isFromOwnBundle(frameFiles);
  }
}
